import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useLocalStorageContextHook from '@/hooks/useLocalStorageContextHook';
import dayjs from 'dayjs';
import getChannelAndMediumFromMailchimp from '@/v2/domains/Analytics/utils/getChannelAndMediumFromMailchimp';
import { utmParams } from '@/v2/domains/Analytics/constants/acquisitionConstants';

import getAnalyticsUserData from './user/getAnalyticsUserData.js';
import { usePlausible } from 'next-plausible';

export default function usePlausibleAnalytics() {
  const router = useRouter();
  const firstPageviewSentRef = useRef(false);
  const [globalContextState, setGlobalContextState] = useState({});
  const [studentContextState, setStudentContextState] = useState({});
  const plausible = usePlausible();

  const { state, actions } = useLocalStorageContextHook({
    contextName: 'plausibleAnalytics',
    defaultState: {
      loaded: false,
      sessionId: null,
      userId: null,
    },
    actions: {
      sendPlausibleEvent: (eventName, props = {}) => {
        if (
          !globalContextState?.loaded ||
          !studentContextState?.loaded ||
          !state.sessionId ||
          !state.userId
        ) {
          return;
        }
        const userData = getAnalyticsUserData(
          globalContextState,
          studentContextState
        );

        const deviceProps = {};
        if (window) {
          deviceProps.device = `${window.screen?.width} x ${window.screen?.height}`;
          deviceProps.viewport = `${window.innerWidth} x ${window.innerHeight}`;
        }

        const eventProps = {
          sessionId: state.sessionId,
          userId: state.userId,
          ...userData,
          ...props,
          ...deviceProps,
        };

        plausible(eventName, {
          props: eventProps,
          u: normalizeUrlForPlausible(),
        });
      },
      setGlobalContextState: (globalContextState) => {
        setGlobalContextState(globalContextState);
      },
      setStudentContextState: (studentContextState) => {
        setStudentContextState(studentContextState);
      },
    },
  });

  useEffect(() => {
    if (state.loaded) {
      const now = dayjs();
      const sessionTime = state.sessionId
        ? dayjs(state.sessionId.split('_')[0], 'YYYYMMDDHHmmss')
        : null;

      const newState = {};
      if (!state.sessionId || now.diff(sessionTime, 'hour') > 0) {
        const random = generateRandomString();
        newState.sessionId = now.format('YYYYMMDDHHmmss_') + random;
      }
      if (!state.userId) {
        newState.userId = generateRandomString();
      }
      if (Object.keys(newState).length > 0) {
        actions.setState(newState);
      }
    }
  }, [state.loaded]);

  useEffect(() => {
    function sendPageview() {
      actions.sendPlausibleEvent('pageview');
    }
    if (
      globalContextState?.loaded &&
      studentContextState?.loaded &&
      (!globalContextState?.user?.student ||
        studentContextState?.subscription?.id) &&
      state.sessionId &&
      state.userId &&
      !firstPageviewSentRef.current
    ) {
      firstPageviewSentRef.current = true;
      sendPageview();
    }

    router.events.on('routeChangeComplete', sendPageview);
    return () => {
      router.events.off('routeChangeComplete', sendPageview);
    };
  }, [
    globalContextState?.loaded,
    globalContextState?.user,
    studentContextState?.loaded,
    studentContextState?.subscription?.id,
    state.sessionId,
    state.userId,
    router.events,
  ]);

  useEffect(() => {
    if (
      globalContextState?.loaded &&
      globalContextState?.user &&
      !globalContextState?.user?.student
    ) {
      actions.sendPlausibleEvent('session_start');
    }
  }, [
    globalContextState?.loaded,
    globalContextState?.user?.student?.id,
    globalContextState?.user?.teacher?.id,
  ]);

  useEffect(() => {
    if (
      globalContextState?.user?.student &&
      studentContextState?.subscription?.id
    ) {
      actions.sendPlausibleEvent('session_start');
    }
  }, [studentContextState?.subscription?.id]);

  return { state, actions };
}

function normalizeUrlForPlausible() {
  const url = new URL(window.location.href);
  const utmMedium = url.searchParams.get(utmParams.medium);
  const utmTerm = url.searchParams.get(utmParams.term);

  const mailchimp = getChannelAndMediumFromMailchimp(utmMedium, utmTerm);

  if (mailchimp) {
    url.searchParams.set(utmParams.source, mailchimp.channel);
    url.searchParams.set(utmParams.medium, mailchimp.medium);
  }

  return url.toString();
}

export function generateRandomString() {
  try {
    let uuid = null;
    if (crypto?.randomUUID) {
      uuid = crypto.randomUUID();
    }
    return uuid || Math.random().toString(36).substring(2, 9);
  } catch (error) {
    return Math.random().toString(36).substring(2, 9);
  }
}
